import { useState, useImperativeHandle, forwardRef, useEffect, useCallback } from "react";
import { useShallow } from "zustand/shallow";

import { AvatarPicker } from "@/shared/components/common/AvatarPicker";
import { Input, Textarea } from "@heroui/react";
import { hooks } from "@/services/api/hooks";
import { useBoundStore } from "@/app/store";
import { resizeAvatarImage } from "@/shared/utils/resizeImage";

import { supabase } from "@/services/supabase/client";

const MAX_BIO_LENGTH = 300;
const MAX_USERNAME_LENGTH = 100;

interface ProfileFormData {
  username: string;
  bio: string;
}

export interface ProfileStepRef {
  validate: () => boolean;
  getData: () => { image: Blob | null; username: string; bio: string };
}

export const ProfileStep = forwardRef<ProfileStepRef>(function ProfileStep(_, ref) {
  const [avatar, setAvatar] = useState<Blob | null>(null);
  const { formData, errors, handleChange, validateForm, initialize } = useProfileStepForm();
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const uploadProfileAvatar = hooks.storage.useUploadProfileAvatar();
  const deleteProfileAvatar = hooks.storage.useDeleteProfileAvatar();
  const { data: profile } = hooks.profiles.useFetchProfile(userId);
  const { data: profileImages } = hooks.storage.useGetUserProfileImages(userId);
  // Get the compressed image ID (if exists) to display
  const compressedImageId = profileImages?.[0]?.id;
  const { data: profileAvatarUrl } = hooks.storage.useGetProfileAvatarUrl(compressedImageId);

  // Initialize form with existing profile data or auth user metadata
  useEffect(() => {
    if (profile?.name || profile?.bio) {
      initialize(profile.name, profile.bio);
    } else {
      (async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const authName =
          user?.user_metadata?.full_name ||
          user?.user_metadata?.name ||
          [user?.user_metadata?.given_name, user?.user_metadata?.family_name].filter(Boolean).join(" ").trim() ||
          "";
        if (authName) {
          initialize(authName, profile?.bio || "");
        }
      })();
    }
  }, [profile, initialize]);

  // Expose validation and data methods to parent
  useImperativeHandle(ref, () => ({
    validate: validateForm,
    getData: () => ({ image: avatar, username: formData.username, bio: formData.bio }),
  }));

  const handleChangeAvatar = async (image: Blob | null) => {
    if (!image || !userId) {
      return;
    }

    const { originalImg, compressedImg } = await resizeAvatarImage(image);

    try {
      // Delete existing profile avatar if it exists
      if (profileImages && profileImages.length > 0) {
        // Delete the compressed image record (which will cascade to delete original)
        await deleteProfileAvatar.mutateAsync({ userId, imageId: profileImages[0].id });
      }

      // Upload new avatar images
      await uploadProfileAvatar.mutateAsync({ userId, originalImg, compressedImg });
      setAvatar(image);
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!userId || !profileImages || profileImages.length === 0) {
      return;
    }

    try {
      // Delete all profile images for this user
      for (const image of profileImages) {
        await deleteProfileAvatar.mutateAsync({ userId, imageId: image.id });
      }
      setAvatar(null);
    } catch (error) {
      console.error("Error deleting avatar:", error);
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="mb-8 flex justify-center">
        <AvatarPicker
          onDeleteImage={handleDeleteAvatar}
          onSelectImage={handleChangeAvatar}
          img={avatar ? URL.createObjectURL(avatar) : profileAvatarUrl || undefined}
          id="avatar-picker"
        />
      </div>
      <Input
        label="Name"
        placeholder="Enter your name (optional)"
        value={formData.username}
        onChange={(e) => handleChange("username", e.target.value)}
        isInvalid={!!errors.username}
        errorMessage={errors.username}
        maxLength={MAX_USERNAME_LENGTH}
        description={`${formData.username.length}/${MAX_USERNAME_LENGTH} characters`}
      />
      <Textarea
        description={`${formData.bio.length}/${MAX_BIO_LENGTH} characters`}
        label="Bio"
        maxLength={MAX_BIO_LENGTH}
        rows={3}
        value={formData.bio}
        onChange={(e) => handleChange("bio", e.target.value)}
      />
    </div>
  );
});

function useProfileStepForm() {
  const [formData, setFormData] = useState<ProfileFormData>({ username: "", bio: "" });
  const [errors, setErrors] = useState<Partial<ProfileFormData>>({});

  const validateForm = () => {
    const newErrors: Partial<ProfileFormData> = {};

    // Name is optional, but if entered, must be at least 3 letters
    if (formData.username.trim() && formData.username.trim().length < 3) {
      newErrors.username = "Name must be at least 3 letters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value })); // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const reset = () => {
    setFormData({ username: "", bio: "" });
    setErrors({});
  };

  const initialize = useCallback((username: string | null, bio: string | null) => {
    setFormData({ username: username || "", bio: bio || "" });
    setErrors({});
  }, []);

  return { formData, errors, handleChange, validateForm, reset, initialize };
}
